import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'ghost' | 'danger';

interface Props extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  variant?: Variant;
  children: ReactNode;
  /**
   * Причина недоступности - готовая строка из словаря.
   *
   * Кнопка при этом остаётся в табуляции и объясняет отказ: мёртвых элементов
   * без объяснения инвариант 9 не допускает. Именно поэтому обычного disabled
   * у компонента нет вовсе - его нельзя выставить, не назвав причину.
   */
  disabledReason?: string | undefined;
}

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-[6px] px-4 text-[13px] font-medium ' +
  'min-h-[var(--spacing-touch)] transition-colors select-none';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-amber text-ink hover:bg-amber2',
  ghost: 'border border-line2 text-txt hover:border-amber hover:text-amber2',
  danger: 'border border-red/60 text-red hover:bg-red/10',
};

export function Button({ variant = 'ghost', children, disabledReason, ...rest }: Props) {
  const blocked = disabledReason !== undefined;
  return (
    <button
      type="button"
      {...rest}
      aria-disabled={blocked}
      title={disabledReason}
      className={`${BASE} ${VARIANTS[variant]} ${blocked ? 'opacity-45 cursor-not-allowed' : ''} ${rest.className ?? ''}`}
      onClick={blocked ? undefined : rest.onClick}
    >
      {children}
    </button>
  );
}
